// Additive failure reporter — observes axios errors on the shared httpClient
// and posts them to the backend's /api/failures/client ingestion endpoint.
//
// Designed to be non-invasive: registered as an extra interceptor on top of
// the existing http.ts setup. Re-throws every error unchanged so existing
// error handling (femaCapture, 401 refresh, etc.) keeps working.
//
// Skips reporting itself to avoid loops. Best-effort fetch — never throws.

import type { AxiosError, AxiosInstance } from 'axios';
import { httpClient } from './http';

const REPORT_PATH = '/api/failures/client';
const MAX_BODY_BYTES = 50_000;

let installed = false;

const SENSITIVE_KEYS = new Set([
  'password',
  'newpassword',
  'currentpassword',
  'token',
  'refreshtoken',
  'accesstoken',
  'authorization',
  'cookie',
  'secret',
  'apikey',
  'cardnumber',
  'cvv',
  'sortcode',
  'accountnumber',
  'nationalinsurancenumber',
]);

function redact(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 6) return '[TRUNCATED:DEPTH]';
  if (typeof value === 'string') return value.length > 4000 ? value.slice(0, 4000) + '…' : value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : redact(v, depth + 1);
  }
  return out;
}

function safeStringify(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  try {
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    return str.length > MAX_BODY_BYTES ? str.slice(0, MAX_BODY_BYTES) + '…' : str;
  } catch {
    return null;
  }
}

interface ReportPayload {
  method: string;
  url: string;
  statusCode: number;
  errorMessage: string;
  requestBody: unknown;
  responseBody: unknown;
  durationMs: number | null;
  userAgent: string;
  pageUrl: string;
  stack: string | null;
}

function buildPayload(error: AxiosError): ReportPayload | null {
  const cfg = error.config;
  if (!cfg) return null;

  const url = cfg.url ?? '';
  // Skip self-reporting and the auth refresh path to avoid feedback loops.
  if (url.includes(REPORT_PATH) || url.includes('refresh-token')) return null;

  const startedAt = (cfg as { _startedAt?: number })._startedAt;
  const durationMs = typeof startedAt === 'number' ? Date.now() - startedAt : null;

  let parsedRequestBody: unknown = null;
  if (cfg.data !== undefined) {
    try {
      parsedRequestBody =
        typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data;
    } catch {
      parsedRequestBody = '[UNPARSABLE]';
    }
  }

  return {
    method: (cfg.method ?? 'GET').toUpperCase(),
    url,
    statusCode: error.response?.status ?? 0,
    errorMessage: error.message ?? 'Unknown error',
    requestBody: redact(parsedRequestBody),
    responseBody: redact(error.response?.data),
    durationMs,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    stack: error.stack ? error.stack.slice(0, 8000) : null,
  };
}

function send(payload: ReportPayload, baseURL: string): void {
  const body = safeStringify(payload);
  if (!body) return;
  const fullUrl =
    (baseURL || '').replace(/\/+$/, '') + REPORT_PATH;
  // Use fetch + keepalive so reports go out even on page unload.
  // No await — fire and forget.
  void fetch(fullUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {
    // Swallow — never let reporter errors surface.
  });
}

export interface ClientFailureReport {
  /**
   * Short identifier for what failed (e.g. 'kyc/camera-blocked'). Stored
   * verbatim as the log row's `url` so support can filter on it.
   */
  context: string;
  /** Human-readable summary. Falls back to `context` if omitted. */
  errorMessage?: string;
  /**
   * Arbitrary structured context. Lands in `requestBody` after server-side
   * redaction (passwords / tokens / PAN are stripped).
   */
  metadata?: Record<string, unknown>;
  /** Optional stack trace. */
  stack?: string;
}

/**
 * Fire-and-forget log of a non-HTTP client event to /api/failures/client.
 * Use for things the axios interceptor can't see — permission denials,
 * SDK init failures, browser API gaps. Never throws; never blocks the UI.
 */
export function reportClientFailure(report: ClientFailureReport): void {
  try {
    const baseURL = (httpClient.defaults.baseURL ?? '').replace(/\/+$/, '');
    const payload: ReportPayload = {
      method: 'CLIENT_EVENT',
      url: report.context,
      statusCode: 0,
      errorMessage: report.errorMessage ?? report.context,
      requestBody: redact(report.metadata ?? {}),
      responseBody: null,
      durationMs: null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      stack: report.stack ?? null,
    };
    send(payload, baseURL);
  } catch {
    // Never let reporter errors surface.
  }
}

/**
 * Install the failure reporter on the shared httpClient.
 * Safe to call once at app boot. Subsequent calls are no-ops.
 */
export function installFailureReporter(client: AxiosInstance = httpClient): void {
  if (installed) return;
  installed = true;

  // Stamp request start time so we can report duration on failure.
  client.interceptors.request.use((cfg) => {
    (cfg as { _startedAt?: number })._startedAt = Date.now();
    return cfg;
  });

  client.interceptors.response.use(
    (res) => res,
    (error: AxiosError) => {
      try {
        const payload = buildPayload(error);
        if (payload) {
          const baseURL = (error.config?.baseURL as string | undefined) ?? '';
          send(payload, baseURL);
        }
      } catch {
        // Never let reporter throw.
      }
      return Promise.reject(error);
    }
  );
}
